import { z } from 'zod';
import { blocksLayoutSchema } from './blocks.js';
import { PLAN_FEATURE_KEYS } from '../plan-features.js';
import {
  CONTENT_TYPES,
  MEDIA_KINDS,
  MEDIA_STATUSES,
  ORGANIZATION_STATUSES,
  SCALING_MODES,
  SCHEDULE_TARGET_TYPES,
  SCREEN_ORIENTATIONS,
  SCREEN_STATUSES,
  SUBSCRIPTION_STATUSES,
  TRANSITIONS,
} from '../constants.js';
import { timeRangeSchema } from '../manifest.js';
import { MENU_CURRENCIES, MENU_FONTS, MENU_LANGUAGES, MENU_TEMPLATES } from '../menu.js';
import { nameSchema, optionalTextSchema, tagsSchema } from './common.js';

/* ------------------------------------------------------------------ organizations */

export const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  status: z.enum(ORGANIZATION_STATUSES),
  timezone: z.string(),
  logoUrl: z.string().nullable(),
  createdAt: z.string(),
  planKey: z.string(),
  planName: z.string(),
  subscriptionStatus: z.enum(SUBSCRIPTION_STATUSES),
  trialEndsAt: z.string().nullable(),
  maxScreens: z.number().int(),
  maxStorageBytes: z.number().int(),
  screensUsed: z.number().int(),
  storageUsedBytes: z.number().int(),
  /** Features the organization's plan includes. */
  capabilities: z.array(z.enum(PLAN_FEATURE_KEYS)),
});
export type Organization = z.infer<typeof organizationSchema>;

export const updateOrganizationSchema = z.object({
  name: nameSchema.optional(),
  timezone: z.string().max(64).optional(),
  logoUrl: z.string().url().nullable().optional(),
});

export const createOrganizationSchema = z.object({
  name: nameSchema,
  timezone: z.string().max(64).default('UTC'),
});

/* ---------------------------------------------------------------------- locations */

export const locationSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  timezone: z.string(),
  tags: z.array(z.string()),
  parentId: z.string().nullable(),
  screenCount: z.number().int().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Location = z.infer<typeof locationSchema>;

export const createLocationSchema = z.object({
  name: nameSchema,
  description: optionalTextSchema,
  address: z.string().trim().max(300).optional().nullable(),
  city: z.string().trim().max(120).optional().nullable(),
  country: z.string().trim().max(120).optional().nullable(),
  timezone: z.string().min(1).max(64),
  tags: tagsSchema.optional(),
  parentId: z.string().uuid().nullable().optional(),
});
export const updateLocationSchema = createLocationSchema.partial();

/* ------------------------------------------------------------------------ screens */

export const screenSettingsSchema = z.object({
  scaling: z.enum(SCALING_MODES).default('fit'),
  rotationDegrees: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).default(0),
  volume: z.number().min(0).max(100).default(0),
  heartbeatIntervalSec: z.number().int().min(10).max(600).optional(),
  contentCheckIntervalSec: z.number().int().min(30).max(3600).optional(),
  maxCacheBytes: z.number().int().positive().optional(),
  screenshotEnabled: z.boolean().optional(),
});
export type ScreenSettings = z.infer<typeof screenSettingsSchema>;

export const screenSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  deviceId: z.string().nullable(),
  locationId: z.string().nullable(),
  locationName: z.string().nullable(),
  locationTimezone: z.string().nullable(),
  orientation: z.enum(SCREEN_ORIENTATIONS),
  width: z.number().int(),
  height: z.number().int(),
  settings: screenSettingsSchema,
  tags: z.array(z.string()),
  status: z.enum(SCREEN_STATUSES),
  lastSeenAt: z.string().nullable(),
  lastSyncAt: z.string().nullable(),
  contentVersion: z.string().nullable(),
  syncedContentVersion: z.string().nullable(),
  syncState: z.string().nullable(),
  playerVersion: z.string().nullable(),
  platform: z.string().nullable(),
  osVersion: z.string().nullable(),
  ipAddress: z.string().nullable(),
  storageTotalBytes: z.number().int().nullable(),
  storageFreeBytes: z.number().int().nullable(),
  cacheBytes: z.number().int().nullable(),
  currentPlaylistId: z.string().nullable(),
  currentPlaylistName: z.string().nullable(),
  currentMediaId: z.string().nullable(),
  currentMediaName: z.string().nullable(),
  screenshotUrl: z.string().nullable(),
  screenshotAt: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Screen = z.infer<typeof screenSchema>;

export const pairScreenSchema = z.object({
  code: z.string().trim().min(4).max(12),
  name: nameSchema,
  locationId: z.string().uuid().nullable().optional(),
  orientation: z.enum(SCREEN_ORIENTATIONS).default('landscape'),
  width: z.number().int().positive().max(16000).optional(),
  height: z.number().int().positive().max(16000).optional(),
  tags: tagsSchema.optional(),
});

export const updateScreenSchema = z.object({
  name: nameSchema.optional(),
  locationId: z.string().uuid().nullable().optional(),
  orientation: z.enum(SCREEN_ORIENTATIONS).optional(),
  width: z.number().int().positive().max(16000).optional(),
  height: z.number().int().positive().max(16000).optional(),
  settings: screenSettingsSchema.partial().optional(),
  tags: tagsSchema.optional(),
  notes: optionalTextSchema,
});

export const screenListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  search: z.string().trim().max(200).optional(),
  status: z.enum(SCREEN_STATUSES).optional(),
  locationId: z.string().uuid().optional(),
  playlistId: z.string().uuid().optional(),
  tag: z.string().trim().max(40).optional(),
  sort: z.enum(['name', 'lastSeenAt', 'createdAt', 'status']).default('name'),
  order: z.enum(['asc', 'desc']).default('asc'),
});

export const screenCommandSchema = z.object({
  command: z.enum(['restart', 'reload', 'sync', 'screenshot', 'clear_cache', 'identify']),
  payload: z.record(z.string(), z.unknown()).optional(),
});

/* ------------------------------------------------------------------------- media */

export const mediaSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  folderId: z.string().nullable(),
  name: z.string(),
  kind: z.enum(MEDIA_KINDS),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  durationMs: z.number().int().nullable(),
  sha256: z.string().nullable(),
  status: z.enum(MEDIA_STATUSES),
  processingError: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  url: z.string().nullable(),
  tags: z.array(z.string()),
  uploadedById: z.string().nullable(),
  uploadedByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Media = z.infer<typeof mediaSchema>;

export const mediaListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(30),
  search: z.string().trim().max(200).optional(),
  kind: z.enum(MEDIA_KINDS).optional(),
  status: z.enum(MEDIA_STATUSES).optional(),
  folderId: z.string().uuid().nullable().optional(),
  tag: z.string().trim().max(40).optional(),
  sort: z.enum(['name', 'createdAt', 'sizeBytes']).default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});

export const updateMediaSchema = z.object({
  name: nameSchema.optional(),
  folderId: z.string().uuid().nullable().optional(),
  tags: tagsSchema.optional(),
});

export const mediaFolderSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  parentId: z.string().nullable(),
  mediaCount: z.number().int().optional(),
  createdAt: z.string(),
});
export const createMediaFolderSchema = z.object({
  name: nameSchema,
  parentId: z.string().uuid().nullable().optional(),
});

/* --------------------------------------------------------------------- playlists */

export const playlistItemInputSchema = z.object({
  id: z.string().uuid().optional(),
  itemType: z.enum(['media', 'slideshow', 'playlist', 'menu']).default('media'),
  mediaId: z.string().uuid().nullable().optional(),
  slideshowId: z.string().uuid().nullable().optional(),
  playlistId: z.string().uuid().nullable().optional(),
  /** A menu board. Its `durationMs`, when set, is how long each page shows. */
  menuId: z.string().uuid().nullable().optional(),
  durationMs: z.number().int().positive().max(24 * 3600 * 1000).nullable().optional(),
  transition: z.enum(TRANSITIONS).default('none'),
  transitionMs: z.number().int().min(0).max(10000).default(600),
  scaling: z.enum(SCALING_MODES).default('fit'),
  muted: z.boolean().default(true),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  daysOfWeek: z.array(z.number().int().min(1).max(7)).max(7).nullable().optional(),
  timeRanges: z.array(timeRangeSchema).max(12).nullable().optional(),
});
export type PlaylistItemInput = z.infer<typeof playlistItemInputSchema>;

export const playlistItemSchema = playlistItemInputSchema.extend({
  id: z.string(),
  position: z.number().int(),
  mediaName: z.string().nullable().optional(),
  mediaKind: z.enum(MEDIA_KINDS).nullable().optional(),
  mediaThumbnailUrl: z.string().nullable().optional(),
  mediaDurationMs: z.number().int().nullable().optional(),
  slideshowName: z.string().nullable().optional(),
  nestedPlaylistName: z.string().nullable().optional(),
  menuName: z.string().nullable().optional(),
});
export type PlaylistItem = z.infer<typeof playlistItemSchema>;

export const playlistSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  loop: z.boolean(),
  tags: z.array(z.string()),
  items: z.array(playlistItemSchema).optional(),
  itemCount: z.number().int().optional(),
  totalDurationMs: z.number().int().optional(),
  assignedScreenCount: z.number().int().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Playlist = z.infer<typeof playlistSchema>;

export const createPlaylistSchema = z.object({
  name: nameSchema,
  description: optionalTextSchema,
  loop: z.boolean().default(true),
  tags: tagsSchema.optional(),
  items: z.array(playlistItemInputSchema).max(500).optional(),
});
export const updatePlaylistSchema = createPlaylistSchema.partial();

/* -------------------------------------------------------------------- slideshows */

export const slideshowItemInputSchema = z.object({
  id: z.string().uuid().optional(),
  mediaId: z.string().uuid(),
  durationMs: z.number().int().positive().max(3600_000).default(10_000),
  transition: z.enum(TRANSITIONS).default('fade'),
  transitionMs: z.number().int().min(0).max(10000).default(600),
  scaling: z.enum(SCALING_MODES).default('fit'),
});
export const slideshowItemSchema = slideshowItemInputSchema.extend({
  id: z.string(),
  position: z.number().int(),
  mediaName: z.string().nullable().optional(),
  mediaThumbnailUrl: z.string().nullable().optional(),
});
export const slideshowSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  loop: z.boolean(),
  items: z.array(slideshowItemSchema).optional(),
  itemCount: z.number().int().optional(),
  totalDurationMs: z.number().int().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Slideshow = z.infer<typeof slideshowSchema>;

export const createSlideshowSchema = z.object({
  name: nameSchema,
  description: optionalTextSchema,
  loop: z.boolean().default(true),
  items: z.array(slideshowItemInputSchema).max(300).optional(),
});
export const updateSlideshowSchema = createSlideshowSchema.partial();

/* -------------------------------------------------------------------------- menus */

const priceSchema = z.number().min(0).max(1_000_000).multipleOf(0.001);
const shortTextSchema = z.string().trim().max(160).nullable().optional();
const ingredientListSchema = z.array(z.string().trim().min(1).max(80)).max(40).default([]);
const variantSchema = z.object({
  label: z.string().trim().min(1).max(30),
  labelAr: z.string().trim().max(30).nullable().optional().transform((v) => v || null),
  detail: z.string().trim().max(30).nullable().optional().transform((v) => v || null),
  price: priceSchema,
});
const variantsSchema = z.array(variantSchema).max(6);
const caloriesSchema = z.number().int().min(0).max(20_000).nullable();
const badgeSchema = z.string().trim().max(24).nullable();

const hexColorSchema = z.string().regex(/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i, 'Use a colour like #ff8800');

export const menuItemInputSchema = z
  .object({
    title: nameSchema,
    titleAr: shortTextSchema,
    description: z.string().trim().max(500).nullable().optional(),
    descriptionAr: z.string().trim().max(500).nullable().optional(),
    ingredients: ingredientListSchema,
    ingredientsAr: ingredientListSchema,
    price: priceSchema,
    discountPrice: priceSchema.nullable().optional(),
    imageMediaId: z.string().uuid().nullable().optional(),
    category: z.string().trim().max(60).nullable().optional(),
    available: z.boolean().default(true),
    variants: variantsSchema.default([]),
    calories: caloriesSchema.optional(),
    badge: badgeSchema.optional(),
    badgeAr: badgeSchema.optional(),
  })
  .refine((v) => v.discountPrice == null || v.discountPrice < v.price, {
    message: 'The discount price must be lower than the original price',
    path: ['discountPrice'],
  });
export type MenuItemInput = z.infer<typeof menuItemInputSchema>;

export const updateMenuItemSchema = z
  .object({
    title: nameSchema,
    titleAr: shortTextSchema,
    description: z.string().trim().max(500).nullable(),
    descriptionAr: z.string().trim().max(500).nullable(),
    ingredients: z.array(z.string().trim().min(1).max(80)).max(40),
    ingredientsAr: z.array(z.string().trim().min(1).max(80)).max(40),
    price: priceSchema,
    discountPrice: priceSchema.nullable(),
    imageMediaId: z.string().uuid().nullable(),
    category: z.string().trim().max(60).nullable(),
    available: z.boolean(),
    variants: variantsSchema,
    calories: caloriesSchema,
    badge: badgeSchema,
    badgeAr: badgeSchema,
  })
  .partial();

export const menuItemSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  title: z.string(),
  titleAr: z.string().nullable(),
  description: z.string().nullable(),
  descriptionAr: z.string().nullable(),
  ingredients: z.array(z.string()),
  ingredientsAr: z.array(z.string()),
  price: z.number(),
  discountPrice: z.number().nullable(),
  imageMediaId: z.string().nullable(),
  imageUrl: z.string().nullable(),
  imageThumbnailUrl: z.string().nullable(),
  category: z.string().nullable(),
  available: z.boolean(),
  variants: z.array(
    z.object({ label: z.string(), labelAr: z.string().nullable(), detail: z.string().nullable(), price: z.number() }),
  ),
  calories: z.number().nullable(),
  badge: z.string().nullable(),
  badgeAr: z.string().nullable(),
  /** Added by a starter menu and not yet edited. */
  isSample: z.boolean(),
  /** How many boards show this item. */
  menuCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MenuItem = z.infer<typeof menuItemSchema>;

export const menuItemListQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  category: z.string().trim().max(60).optional(),
});

export const menuThemeSchema = z.object({
  backgroundColor: hexColorSchema,
  surfaceColor: hexColorSchema,
  textColor: hexColorSchema,
  primaryColor: hexColorSchema,
  accentColor: hexColorSchema,
  fontId: z.enum(MENU_FONTS.map((f) => f.id) as [string, ...string[]]),
  backgroundMediaId: z.string().uuid().nullable(),
  variant: z.string().trim().max(40).nullable().optional(),
});

export const menuSettingsSchema = z.object({
  title: z.string().trim().max(120).nullable(),
  titleAr: z.string().trim().max(120).nullable(),
  language: z.enum(MENU_LANGUAGES),
  currency: z.enum(MENU_CURRENCIES as [string, ...string[]]),
  pageDurationMs: z.number().int().min(3000).max(600_000),
  showDescriptions: z.boolean(),
  showIngredients: z.boolean(),
});

// The predefined designs, plus `custom` for a board drawn from a saved design.
const menuTemplateIdSchema = z.enum(['custom', ...MENU_TEMPLATES.map((t) => t.id)] as [string, ...string[]]);

const optionalLine = z.string().trim().max(120).nullable().optional().transform((v) => v || null);

export const menuSectionSchema = z.object({
  /** Chosen by the client (any short string), so new sections can be referenced at once. */
  id: z.string().trim().min(1).max(40),
  title: optionalLine,
  titleAr: optionalLine,
  subtitle: optionalLine,
  subtitleAr: optionalLine,
  imageMediaId: z.string().uuid().nullable().optional().transform((v) => v ?? null),
});

/** Partial by design: a field left out keeps its saved value, so it must stay undefined. */
const brandLine = z.string().trim().max(120).nullable().optional().transform((v) => (v === undefined ? undefined : v || null));

export const menuBrandSchema = z.object({
  name: brandLine,
  nameAr: brandLine,
  tagline: brandLine,
  taglineAr: brandLine,
  logoMediaId: z.string().uuid().nullable().optional(),
});

export const menuEntrySchema = z.object({
  itemId: z.string().uuid(),
  sectionId: z.string().max(40).nullable().optional().transform((v) => v ?? null),
});

export const createMenuSchema = z.object({
  name: nameSchema,
  templateId: menuTemplateIdSchema.optional(),
  theme: menuThemeSchema.partial().optional(),
  settings: menuSettingsSchema.partial().optional(),
  /** Items in display order, all in the first section. `entries` wins when both are given. */
  itemIds: z.array(z.string().uuid()).max(200).optional(),
  entries: z.array(menuEntrySchema).max(200).optional(),
  sections: z.array(menuSectionSchema).max(20).optional(),
  featuredItemId: z.string().uuid().nullable().optional(),
  brand: menuBrandSchema.optional(),
  decorMediaIds: z.array(z.string().uuid()).max(6).optional(),
  /** With `templateId: "custom"`, the saved design the board uses. */
  customDesignId: z.string().uuid().nullable().optional(),
  /** With `templateId: "blocks"`, the board's own grid of blocks ("Build your own"). */
  blocks: blocksLayoutSchema.nullable().optional(),
  /**
   * Create only: fill the board with the design's starter menu (sample items, sections and
   * real photos), so it looks finished at once. Anything else sent alongside is ignored.
   */
  starter: z.boolean().optional(),
  /** With `starter` on a custom board: whose sample menu to fill it with. */
  sampleTemplateId: z.string().max(40).optional(),
});
export const updateMenuSchema = createMenuSchema.partial();

export const menuSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  templateId: z.string(),
  theme: menuThemeSchema.extend({ fontId: z.string() }),
  backgroundUrl: z.string().nullable(),
  settings: menuSettingsSchema.extend({ currency: z.string() }),
  itemIds: z.array(z.string()),
  entries: z.array(z.object({ itemId: z.string(), sectionId: z.string().nullable() })),
  sections: z.array(
    z.object({
      id: z.string(),
      title: z.string().nullable(),
      titleAr: z.string().nullable(),
      subtitle: z.string().nullable(),
      subtitleAr: z.string().nullable(),
      imageMediaId: z.string().nullable(),
    }),
  ),
  featuredItemId: z.string().nullable(),
  brand: z.object({
    name: z.string().nullable(),
    nameAr: z.string().nullable(),
    tagline: z.string().nullable(),
    taglineAr: z.string().nullable(),
    logoMediaId: z.string().nullable(),
  }),
  decorMediaIds: z.array(z.string()),
  customDesignId: z.string().nullable(),
  /** The design itself, for previews, when the board uses one. */
  customDesign: z
    .object({ id: z.string(), name: z.string(), document: z.unknown(), mediaUrls: z.record(z.string(), z.string().nullable()) })
    .nullable()
    .optional(),
  /** The board's "Build your own" layout, kept when it switches to another design. */
  blocks: z.unknown().nullable().optional(),
  /** Signed URLs for the board's own pictures (logo, decorations, section pictures). */
  mediaUrls: z.record(z.string(), z.string().nullable()),
  /** Present on the single-board read: the items in board order, for the editor and preview. */
  items: z.array(menuItemSchema).optional(),
  itemCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Menu = z.infer<typeof menuSchema>;

/* --------------------------------------------------------------------- schedules */

export const scheduleTargetInputSchema = z.object({
  targetType: z.enum(SCHEDULE_TARGET_TYPES),
  screenId: z.string().uuid().nullable().optional(),
  locationId: z.string().uuid().nullable().optional(),
  tag: z.string().trim().max(40).nullable().optional(),
});
export type ScheduleTargetInput = z.infer<typeof scheduleTargetInputSchema>;

export const scheduleTargetSchema = scheduleTargetInputSchema.extend({
  id: z.string(),
  label: z.string().optional(),
});

export const scheduleSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  enabled: z.boolean(),
  priority: z.number().int(),
  always: z.boolean(),
  contentType: z.enum(CONTENT_TYPES),
  contentId: z.string(),
  contentName: z.string().nullable().optional(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  daysOfWeek: z.array(z.number().int()).nullable(),
  timeRanges: z.array(timeRangeSchema).nullable(),
  targets: z.array(scheduleTargetSchema).optional(),
  targetScreenCount: z.number().int().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Schedule = z.infer<typeof scheduleSchema>;

/** A schedule as one screen sees it: how it reaches the screen, and whether it is the screen's own. */
export const screenScheduleSchema = scheduleSchema.extend({
  via: z.array(
    z.object({
      targetType: z.enum(SCHEDULE_TARGET_TYPES),
      /** The group, location or screen named by the target; null for "all screens". */
      label: z.string().nullable(),
    }),
  ),
  /** Targets this screen alone, so editing it from the screen changes nothing else. */
  own: z.boolean(),
});
export type ScreenSchedule = z.infer<typeof screenScheduleSchema>;

export const screenSchedulesSchema = z.object({
  timezone: z.string(),
  schedules: z.array(screenScheduleSchema),
});
export type ScreenSchedules = z.infer<typeof screenSchedulesSchema>;

export const createScheduleSchema = z
  .object({
    name: nameSchema,
    description: optionalTextSchema,
    enabled: z.boolean().default(true),
    priority: z.number().int().min(0).max(1000).default(1),
    always: z.boolean().default(false),
    contentType: z.enum(CONTENT_TYPES),
    contentId: z.string().uuid(),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    daysOfWeek: z.array(z.number().int().min(1).max(7)).max(7).nullable().optional(),
    timeRanges: z.array(timeRangeSchema).max(12).nullable().optional(),
    targets: z.array(scheduleTargetInputSchema).min(1).max(500),
  })
  .refine((v) => !v.startDate || !v.endDate || v.startDate <= v.endDate, {
    message: 'End date must not be before the start date',
    path: ['endDate'],
  });

export const updateScheduleSchema = z.object({
  name: nameSchema.optional(),
  description: optionalTextSchema,
  enabled: z.boolean().optional(),
  priority: z.number().int().min(0).max(1000).optional(),
  always: z.boolean().optional(),
  contentType: z.enum(CONTENT_TYPES).optional(),
  contentId: z.string().uuid().optional(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  daysOfWeek: z.array(z.number().int().min(1).max(7)).max(7).nullable().optional(),
  timeRanges: z.array(timeRangeSchema).max(12).nullable().optional(),
  targets: z.array(scheduleTargetInputSchema).min(1).max(500).optional(),
});

/** Quick assignment: set the always-on playlist for a set of targets. */
export const assignContentSchema = z.object({
  contentType: z.enum(CONTENT_TYPES).default('playlist'),
  contentId: z.string().uuid(),
  targets: z.array(scheduleTargetInputSchema).min(1).max(500),
  /** Replace existing always-on assignments for these targets. */
  replace: z.boolean().default(true),
  name: nameSchema.optional(),
});

/**
 * What an assignment changed, so it can be reversed exactly: the schedule it made, the older
 * schedules it deleted, and the targets it took off the ones it kept.
 */
export const assignUndoSchema = z.object({
  createdScheduleId: z.string().uuid(),
  restoreSchedules: z.array(z.object({ id: z.string().uuid(), enabled: z.boolean() })).max(500),
  restoreTargets: z.array(scheduleTargetInputSchema.extend({ scheduleId: z.string().uuid() })).max(2000),
});
export type AssignUndo = z.infer<typeof assignUndoSchema>;

/* ------------------------------------------------------------------ screen groups */

export const screenGroupSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  tag: z.string(),
  description: z.string().nullable(),
  screenCount: z.number().int().optional(),
  createdAt: z.string(),
});
export const createScreenGroupSchema = z.object({
  name: nameSchema,
  tag: z.string().trim().min(1).max(40),
  description: optionalTextSchema,
});
